// src/pages/Pricing.jsx
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useAuth } from "@clerk/clerk-react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Check,
  Loader2,
  ShieldCheck,
  ChevronRight,
  Info,
  Star,
  Crown,
  Zap,
  Sparkles,
  Image as ImageIcon,
  Film,
  Link as LinkIcon,
} from "lucide-react";

/** Brand */
const BG = "#0C0C1A";
const PANEL = "rgba(22,22,40,0.72)";
const CARD = "#141428";
const BORDER = "#2A2A40";
/** Brand pink accent */
const ACCENT = "#F82E88";
const GLOW_SOFT = "rgba(248,46,136,0.30)";
const GREEN = "#22c55e";
const RED = "#ef4444";

/** Fixed top bar height (for padding to avoid headline overlap) */
const TOP_BAR_HEIGHT = 84;

const FALLBACK_PORTAL_URL =
  process.env.REACT_APP_STRIPE_PORTAL_URL ||
  "https://billing.stripe.com/p/login/test_00w3cvadgaPNgysgZC1wY00";

/**
 * ✅ Unify API base with Admin pages.
 * This prevents Admin writing to one backend while Pricing reads from another.
 */
const API_BASE =
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  (typeof window !== "undefined" && window._API_BASE) ||
  "https://ecom-ads-ai-production.up.railway.app";

/** ✅ Default signup bonus (backend may override via /plans or /status) */
const DEFAULT_SIGNUP_BONUS = { images: 5, videos: 1, urls: 1 };

function formatTs(tsSec) {
  if (!tsSec) return "";
  const d = new Date(tsSec * 1000);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** Format money */
const euro2 = (n) =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n || 0));

/** Combine caps into a single "capacity" score for upgrade/downgrade decisions */
const planCapacityScore = (plan) =>
  (plan.imagesCap || 0) * 2 + (plan.videosCap || 0) * 5 + (plan.analyzeCap || 0);

/** Robust Euro parser for labels (fallback only) */
const parseEuroFromLabel = (label) => {
  if (!label) return null;
  const m = String(label).match(/€\s*([\d.,]+)/);
  if (!m) return null;
  const s = m[1].trim();

  // If both '.' and ',' → European: '.' thousands, ',' decimals
  if (s.includes(".") && s.includes(",")) {
    return Number.parseFloat(s.replace(/\./g, "").replace(",", "."));
  }
  // Only comma → decimal separator
  if (s.includes(",")) return Number.parseFloat(s.replace(",", "."));
  return Number.parseFloat(s);
};

export default function Pricing() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [currentPriceId, setCurrentPriceId] = useState(null);
  const [subStatus, setSubStatus] = useState(null);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState(null);
  const [buttonLoading, setButtonLoading] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  /** unlimited flags from backend */
  const [unlimited, setUnlimited] = useState({ isUnlimited: false });

  /** ✅ signup bonus (free add-ons granted to every new user) */
  const [signupBonus, setSignupBonus] = useState(DEFAULT_SIGNUP_BONUS);
  const [signupBonusGranted, setSignupBonusGranted] = useState(false);

  /** monthly | yearly — default YEARLY */
  const [billingCycle, setBillingCycle] = useState("yearly");

  const pollEndTsRef = useRef(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();

      const plansReq = axios.get(`${API_BASE}/api/billing/plans`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      const addonsReq = axios.get(`${API_BASE}/api/billing/topups`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      const statusReq = axios
        .get(`${API_BASE}/api/billing/status`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        })
        .catch(() => null);

      const [plansRes, addonsRes, statusRes] = await Promise.all([
        plansReq,
        addonsReq,
        statusReq,
      ]);

      // ✅ Plans: now trust backend for caps + prices (monthly/yearly EUR) if provided.
      const fetchedPlans = (plansRes?.data?.plans || []).map((p) => {
        const m = Number.isFinite(+p.priceEurMonthly)
          ? +p.priceEurMonthly
          : parseEuroFromLabel(p.priceLabelMonthly);
        const y = Number.isFinite(+p.priceEurYearly)
          ? +p.priceEurYearly
          : parseEuroFromLabel(p.priceLabelYearly);

        return {
          ...p,
          priceEurMonthly: Number.isFinite(m) ? m : null,
          priceEurYearly: Number.isFinite(y) ? y : null,
        };
      });

      setPlans(fetchedPlans);

      // ✅ Add-ons: support admin-changed addon prices + amounts.
      // - price: prefer unitPrice/priceEur/price over parsing label
      // - amount: prefer amount; else derive from bump.{images|videos|urls} (even if id changes)
      const toNonNegInt = (v) => {
        const n = Number(v);
        if (!Number.isFinite(n)) return null;
        return Math.max(0, Math.floor(n));
      };

      const inferAddonFeature = (t) => {
        const bump = t?.bump || null;

        // If bump explicitly indicates the feature, trust it first
        const bi = toNonNegInt(bump?.images);
        const bv = toNonNegInt(bump?.videos);
        const bu = toNonNegInt(bump?.urls);

        if (bi != null) return "images";
        if (bv != null) return "videos";
        if (bu != null) return "urls";

        // Fallback to id patterns (legacy)
        const id = String(t?.id || "").toLowerCase();
        if (id.startsWith("images_") || id === "images_10") return "images";
        if (id.startsWith("video_") || id === "video_1") return "videos";
        if (id.startsWith("url_") || id === "url_1") return "urls";

        return null;
      };

      const inferAddonAmount = (t, feature) => {
        // direct amount (preferred)
        const direct = Number.isFinite(+t.amount)
          ? Math.max(0, Math.floor(+t.amount))
          : null;
        if (direct != null) return direct;

        // fallback: bump-based amount
        const bump = t?.bump || null;
        if (feature === "images") return toNonNegInt(bump?.images);
        if (feature === "videos") return toNonNegInt(bump?.videos);
        if (feature === "urls") return toNonNegInt(bump?.urls);

        // final fallback: try any bump key
        const bi = toNonNegInt(bump?.images);
        const bv = toNonNegInt(bump?.videos);
        const bu = toNonNegInt(bump?.urls);
        return bi != null ? bi : bv != null ? bv : bu != null ? bu : null;
      };

      const inferUnitPrice = (t) => {
        const candidates = [
          t.unitPrice,
          t.priceEur,
          t.price_eur,
          t.unit_price,
          t.price,
          t.amountEur,
        ];
        for (const c of candidates) {
          const n = Number(c);
          if (Number.isFinite(n)) return n;
        }
        return parseEuroFromLabel(t.label);
      };

      const fetchedAddons = (addonsRes?.data?.topups || [])
        .filter((t) => String(t.type || "").startsWith("addon"))
        .map((t) => {
          const feature = inferAddonFeature(t);
          const unit = inferUnitPrice(t);
          const amt = inferAddonAmount(t, feature);

          return {
            ...t,
            addonFeature: feature, // "images" | "videos" | "urls" | null
            unitPrice: Number.isFinite(unit) ? unit : null,
            amount: amt,
          };
        });

      setAddons(fetchedAddons);

      const st = statusRes?.data || {};
      setCurrentPriceId(st.currentPriceId || null);
      setSubStatus(st.activeSubscription?.status || null);
      setCancelAtPeriodEnd(Boolean(st.activeSubscription?.cancel_at_period_end));
      setCurrentPeriodEnd(st.activeSubscription?.current_period_end || null);
      if (st.unlimited) setUnlimited(st.unlimited);

      // ✅ FIX: Signup bonus was not updating because backend may expose it under
      // tier_caps / tierCaps / caps AND also under different keys than signup_bonus.
      // We now accept the same shapes/aliases used in backoffice storage.
      const plansData = plansRes?.data || {};
      const statusData = st || {};

      const pickFirst = (...vals) => {
        for (const v of vals) if (v !== undefined && v !== null) return v;
        return null;
      };

      const capsFromPlans =
        plansData?.caps ||
        plansData?.tier_caps ||
        plansData?.tierCaps ||
        plansData?.tiercaps ||
        null;

      const capsFromStatus =
        statusData?.caps ||
        statusData?.tier_caps ||
        statusData?.tierCaps ||
        statusData?.tiercaps ||
        null;

      const sbRaw = pickFirst(
        // direct
        plansData.signupBonus,
        plansData.signup_bonus,
        statusData.signupBonus,
        statusData.signup_bonus,

        // common wrappers
        capsFromPlans?.signupBonus,
        capsFromPlans?.signup_bonus,
        capsFromStatus?.signupBonus,
        capsFromStatus?.signup_bonus,

        // aliases seen in admin/backoffice
        capsFromPlans?.trial_bonus,
        capsFromPlans?.trial_offer,
        capsFromPlans?.trial_offer_bonus,
        capsFromPlans?.free_trial_bonus,
        capsFromPlans?.free_trial_offer,
        capsFromPlans?.trial_offer_bonus,
        capsFromPlans?.signup_addons,

        capsFromStatus?.trial_bonus,
        capsFromStatus?.trial_offer,
        capsFromStatus?.trial_offer_bonus,
        capsFromStatus?.free_trial_bonus,
        capsFromStatus?.free_trial_offer,
        capsFromStatus?.trial_offer_bonus,
        capsFromStatus?.signup_addons,

        DEFAULT_SIGNUP_BONUS
      );

      const sb = {
        images: Number.isFinite(+sbRaw?.images)
          ? Math.max(0, Math.floor(+sbRaw.images))
          : 0,
        videos: Number.isFinite(+sbRaw?.videos)
          ? Math.max(0, Math.floor(+sbRaw.videos))
          : 0,
        urls: Number.isFinite(+sbRaw?.urls)
          ? Math.max(0, Math.floor(+sbRaw.urls))
          : 0,
      };

      setSignupBonus(sb);

      const aTotal = statusData?.addons?.total || null;
      if (aTotal) {
        const got =
          (Number(aTotal.images || 0) >= sb.images || sb.images === 0) &&
          (Number(aTotal.videos || 0) >= sb.videos || sb.videos === 0) &&
          (Number(aTotal.urls || 0) >= sb.urls || sb.urls === 0);
        setSignupBonusGranted(Boolean(got));
      } else {
        setSignupBonusGranted(false);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Failed to load pricing:", e?.message || e);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    load();
  }, [load]);

  // Post-checkout handling (no credits, just refresh billing state)
  useEffect(() => {
    const url = new URL(window.location.href);
    const status = url.searchParams.get("status");
    const isSuccessPath = location.pathname === "/billing/success";

    if (status === "success" || isSuccessPath) {
      (async () => {
        try {
          await load();
        } finally {
          if (status) {
            url.searchParams.delete("status");
            window.history.replaceState({}, "", url.toString());
          }
          if (isSuccessPath) navigate("/pricing", { replace: true });
          pollEndTsRef.current = Date.now() + 60_000; // poll for up to 60s
        }
      })();
    }
  }, [location.pathname, load, navigate]);

  // Poll billing status for a bit after success
  useEffect(() => {
    let id;
    const tick = async () => {
      if (Date.now() > pollEndTsRef.current) return;
      await load();
      id = window.setTimeout(tick, 6000);
    };
    if (pollEndTsRef.current > Date.now()) id = window.setTimeout(tick, 3000);
    return () => clearTimeout(id);
  }, [load]);

  const goCheckout = useCallback(
    async ({ mode, priceId, quantity = 1, overrides = null }) => {
      try {
        setButtonLoading(priceId);
        const token = await getToken();

        // ✅ overrides are sent to backend so it can charge the updated admin-configured amounts
        const res = await axios.post(
          `${API_BASE}/api/billing/checkout`,
          {
            mode,
            priceId,
            quantity,
            overrides: overrides || null,
            successUrl: `${window.location.origin}/billing/success`,
            cancelUrl: `${window.location.origin}/billing/cancel`,
          },
          { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
        );
        window.location.href = res.data.url;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Checkout failed:", e?.response?.data || e?.message || e);
        setButtonLoading(null);
        alert(e?.response?.data?.error || "Checkout failed. Please try again.");
      }
    },
    [getToken]
  );

  const openPortal = useCallback(async () => {
    try {
      setPortalLoading(true);
      const token = await getToken();
      const res = await axios.post(
        `${API_BASE}/api/billing/portal`,
        { returnUrl: `${window.location.origin}/pricing` },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      window.location.href = res.data.url;
    } catch {
      window.location.href = FALLBACK_PORTAL_URL;
    } finally {
      setPortalLoading(false);
    }
  }, [getToken]);

  const isCurrent = (priceId) => currentPriceId && currentPriceId === priceId;
  const currentPlan =
    plans.find(
      (p) =>
        p.monthlyPriceId === currentPriceId || p.yearlyPriceId === currentPriceId
    ) || null;
  const currentCapacityScore = currentPlan ? planCapacityScore(currentPlan) : 0;

  /** Button rules for plans, using caps (no credits) */
  const planAction = (plan) => {
    const chosenPriceId =
      billingCycle === "yearly" ? plan.yearlyPriceId : plan.monthlyPriceId;

    if (unlimited?.isUnlimited) {
      return {
        label: "Unlimited access enabled",
        onClick: () => {},
        disabled: true,
        kind: "downgrade",
        loading: false,
        helper: "Your account bypasses subscriptions & payments",
      };
    }

    if (!currentPriceId) {
      return {
        label: billingCycle === "yearly" ? "Get yearly plan" : "Get monthly plan",
        onClick: () =>
          goCheckout({
            mode: "subscription",
            priceId: chosenPriceId,
            overrides: {
              kind: "tier",
              tier: plan.id,
              cycle: billingCycle,
              priceEurMonthly: plan.priceEurMonthly,
              priceEurYearly: plan.priceEurYearly,
            },
          }),
        disabled: buttonLoading === chosenPriceId,
        kind: "accent",
        loading: buttonLoading === chosenPriceId,
        helper: billingCycle === "yearly" ? "2 months free" : "",
      };
    }

    const isCurrentAnyCycle =
      currentPriceId === plan.monthlyPriceId ||
      currentPriceId === plan.yearlyPriceId;
    const switchingCycle = isCurrentAnyCycle && currentPriceId !== chosenPriceId;

    if (currentPriceId === chosenPriceId) {
      return {
        label: "Manage subscription",
        onClick: openPortal,
        disabled: portalLoading,
        kind: "accent",
        loading: portalLoading,
        helper: cancelAtPeriodEnd
          ? `Ends ${formatTs(currentPeriodEnd)}`
          : `Renews ${formatTs(currentPeriodEnd)}`,
      };
    }

    const targetScore = planCapacityScore(plan);
    const isCapacityUpgrade = targetScore > currentCapacityScore;

    if (switchingCycle && billingCycle === "yearly") {
      return {
        label: "Switch to yearly",
        onClick: () =>
          goCheckout({
            mode: "subscription",
            priceId: chosenPriceId,
            overrides: {
              kind: "tier",
              tier: plan.id,
              cycle: billingCycle,
              priceEurMonthly: plan.priceEurMonthly,
              priceEurYearly: plan.priceEurYearly,
            },
          }),
        disabled: buttonLoading === chosenPriceId,
        kind: "upgrade",
        loading: buttonLoading === chosenPriceId,
        helper: "Prorated by Stripe",
      };
    }

    if (isCapacityUpgrade) {
      return {
        label: "Upgrade",
        onClick: () =>
          goCheckout({
            mode: "subscription",
            priceId: chosenPriceId,
            overrides: {
              kind: "tier",
              tier: plan.id,
              cycle: billingCycle,
              priceEurMonthly: plan.priceEurMonthly,
              priceEurYearly: plan.priceEurYearly,
            },
          }),
        disabled: buttonLoading === chosenPriceId,
        kind: "upgrade",
        loading: buttonLoading === chosenPriceId,
        helper: "Prorated by Stripe",
      };
    }

    return {
      label: "Downgrade (use Manage)",
      onClick: () => {},
      disabled: true,
      kind: "downgrade",
      loading: false,
      helper: "Open Manage Subscription to downgrade",
    };
  };

  // Features (shared across plans) + extra highlights
  const allFeatures = useMemo(() => {
    const base = plans?.[0]?.features || [];
    const extras = [
      "9:16, 4:5, 16:9, 1:1 Aspect Ratios",
      "Video captions",
      "Custom AI video scripts",
      "500+ avatars & voices",
    ];
    return [...new Set([...base, ...extras])];
  }, [plans]);

  /** Monthly/Yearly toggle (affects subscriptions only, not add-ons) */
  const BillingToggle = (
    <div className="flex items-center justify-center mt-6">
      <div
        className="flex items-center p-1 rounded-2xl border"
        style={{ borderColor: BORDER, background: PANEL }}
      >
        <button
          className={`px-4 py-2 rounded-xl font-semibold transition ${
            billingCycle === "monthly" ? "text-white" : "text-gray-300"
          }`}
          style={{
            background: billingCycle === "monthly" ? ACCENT : "transparent",
          }}
          onClick={() => setBillingCycle("monthly")}
          disabled={unlimited?.isUnlimited}
          title={unlimited?.isUnlimited ? "Unlimited enabled" : "Monthly"}
        >
          Monthly
        </button>
        <button
          className={`px-4 py-2 rounded-xl font-semibold transition ${
            billingCycle === "yearly" ? "text-white" : "text-gray-300"
          }`}
          style={{
            background: billingCycle === "yearly" ? ACCENT : "transparent",
          }}
          onClick={() => setBillingCycle("yearly")}
          disabled={unlimited?.isUnlimited}
          title={unlimited?.isUnlimited ? "Unlimited enabled" : "2 months free"}
        >
          Yearly <span className="ml-1 opacity-80">(2 months free)</span>
        </button>
      </div>
    </div>
  );

  /** Add-on quantity state, keyed by priceId */
  const [addonQty, setAddonQty] = useState({});

  /** Safe qty getters/setters (add-ons are one-off; not tied to billing cycle) */
  const getQty = (priceId) => Math.max(1, addonQty[priceId] || 1);
  const setQty = (priceId, q) =>
    setAddonQty((s) => ({ ...s, [priceId]: Math.min(50, Math.max(1, q | 0)) }));

  /** Dropdown quantity selector */
  function QtySelect({ value, onChange, disabled }) {
    return (
      <select
        value={value}
        disabled={disabled}
        onChange={(e) =>
          onChange(Math.min(50, Math.max(1, Number(e.target.value) || 1)))
        }
        className="appearance-none rounded-xl px-4 py-2 bg-[#0f0f21] text-white border w-full"
        style={{ borderColor: BORDER }}
        aria-label="Quantity"
      >
        {Array.from({ length: 50 }, (_, i) => i + 1).map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </select>
    );
  }

  return (
    <div
      className="relative min-h-screen"
      style={{ background: BG, paddingTop: `${TOP_BAR_HEIGHT + 16}px` }}
    >
      {/* Background glow */}
      <div className="pointer-events-none fixed inset-0 -z-10">
        <div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(900px 600px at 60% -180px, ${GLOW_SOFT} 0%, rgba(248,46,136,0.16) 28%, rgba(248,46,136,0.06) 46%, transparent 65%)`,
          }}
        />
      </div>

      {/* Fixed top-left actions */}
      <div className="fixed left-4 top-4 z-50 flex items-center gap-3">
        <button
          className="inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-lg text-white font-semibold"
          style={{ background: "#2A2A40", border: "1px solid #3a3a4d" }}
          onClick={() => navigate("/analyze")}
        >
          Back to Products
        </button>

        {/* Manage subscription button and current tier pill */}
        {currentPlan && !unlimited?.isUnlimited && (
          <>
            <button
              className="inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-lg text-white font-semibold disabled:opacity-80"
              style={{
                background: "#b45309",
                boxShadow: "0 8px 18px rgba(180,83,9,0.35)",
              }}
              onClick={openPortal}
              disabled={portalLoading}
              title="Manage subscription"
            >
              {portalLoading ? (
                <>
                  <Loader2 className="animate-spin" size={16} />
                  Opening portal…
                </>
              ) : (
                <>Manage subscription</>
              )}
            </button>

            <div
              className="inline-flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 px-3 py-2 rounded-xl border text-white"
              style={{ borderColor: BORDER, background: "#17172D" }}
            >
              <span className="font-semibold">
                Current tier:{" "}
                <span className="text-white/90">{currentPlan.name}</span>
              </span>
              <span className="text-gray-300 text-sm">
                {cancelAtPeriodEnd
                  ? `Cancels on ${formatTs(currentPeriodEnd)}`
                  : subStatus
                  ? `Status: ${subStatus} • Renews on ${formatTs(currentPeriodEnd)}`
                  : "No active subscription"}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Title + secure message */}
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pb-2 text-center">
        <h1 className="text-white text-3xl sm:text-5xl font-extrabold tracking-tight">
          Scale your product photos & ads with AI
        </h1>
        <p className="text-gray-300 mt-3 max-w-2xl text-base mx-auto">
          Three simple plans. Same toolkit — different monthly capacity.
        </p>

        {/* Unlimited banner */}
        {unlimited?.isUnlimited && (
          <div
            className="mx-auto mt-4 inline-flex items-center gap-2 rounded-2xl px-4 py-2 border"
            style={{ borderColor: BORDER, background: "#121228" }}
          >
            <Sparkles className="text-emerald-400" size={18} />
            <span className="text-sm text-white">
              Unlimited access enabled — Stripe actions are disabled for this account.
            </span>
          </div>
        )}

        {/* Toggle */}
        {BillingToggle}

        {/* Secure message */}
        <div className="flex items-center justify-center mt-6 relative z-10">
          <div
            className="flex items-center gap-3 px-4 py-3 rounded-2xl border"
            style={{ borderColor: BORDER, background: "#121228" }}
          >
            <ShieldCheck size={20} style={{ color: GREEN }} />
            <span className="text-white font-semibold text-base sm:text-lg">
              Guaranteed safe &amp; secure checkout — powered by Stripe
            </span>
          </div>
        </div>

        {/* ✅ Free signup bonus highlight */}
        {!loading && (
          <div className="mt-6">
            <div
              className="mx-auto max-w-4xl rounded-3xl border p-5 sm:p-6"
              style={{
                borderColor: `${GREEN}55`,
                background:
                  "linear-gradient(180deg, rgba(34,197,94,0.10) 0%, rgba(18,18,40,0.92) 100%)",
                boxShadow: "0 14px 34px rgba(0,0,0,0.30)",
              }}
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="text-left">
                  <div className="flex items-center gap-2">
                    <span
                      className="px-3 py-1 rounded-full text-[11px] font-extrabold tracking-wide uppercase"
                      style={{
                        background: GREEN,
                        color: "#04110D",
                        boxShadow: "0 10px 24px rgba(34,197,94,0.30)",
                        letterSpacing: "0.06em",
                      }}
                    >
                      Free on signup
                    </span>
                    {signupBonusGranted && (
                      <span
                        className="px-3 py-1 rounded-full text-[11px] font-bold"
                        style={{
                          background: "#0f2b1a",
                          border: "1px solid rgba(34,197,94,0.45)",
                          color: "#bbf7d0",
                        }}
                      >
                        Already added to your account
                      </span>
                    )}
                  </div>

                  <div className="mt-2 text-white text-xl sm:text-2xl font-extrabold">
                    Start instantly — no credit card needed
                  </div>
                  <div className="mt-1 text-gray-200/90 text-sm sm:text-base">
                    Every new account automatically receives a one-time starter bundle.
                  </div>
                </div>
              </div>

              <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div
                  className="rounded-2xl border px-4 py-4 flex items-center justify-between"
                  style={{
                    background: "#0f0f21",
                    borderColor: BORDER,
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
                  }}
                >
                  <div className="flex items-center gap-3">
                    <ImageIcon className="w-6 h-6 text-gray-200" />
                    <div className="text-left">
                      <div className="text-gray-200 text-sm font-semibold">
                        AI Images
                      </div>
                      <div className="text-gray-500 text-xs">one-time bonus</div>
                    </div>
                  </div>
                  <div className="text-white text-3xl font-extrabold tracking-tight">
                    {signupBonus.images}
                  </div>
                </div>

                <div
                  className="rounded-2xl border px-4 py-4 flex items-center justify-between"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(248,46,136,0.10) 0%, rgba(20,20,40,0.85) 100%)",
                    borderColor: `${ACCENT}55`,
                    boxShadow: `0 0 0 1px ${ACCENT}22 inset`,
                  }}
                >
                  <div className="flex items-center gap-3">
                    <Film className="w-6 h-6 text-gray-200" />
                    <div className="text-left">
                      <div className="text-gray-200 text-sm font-semibold">
                        AI Videos
                      </div>
                      <div className="text-gray-500 text-xs">one-time bonus</div>
                    </div>
                  </div>
                  <div className="text-white text-3xl font-extrabold tracking-tight">
                    {signupBonus.videos}
                  </div>
                </div>

                <div
                  className="rounded-2xl border px-4 py-4 flex items-center justify-between"
                  style={{
                    background: "#0f0f21",
                    borderColor: BORDER,
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
                  }}
                >
                  <div className="flex items-center gap-3">
                    <LinkIcon className="w-6 h-6 text-gray-200" />
                    <div className="text-left">
                      <div className="text-gray-200 text-sm font-semibold">
                        Product Pages
                      </div>
                      <div className="text-gray-500 text-xs">one-time bonus</div>
                    </div>
                  </div>
                  <div className="text-white text-3xl font-extrabold tracking-tight">
                    {signupBonus.urls}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Plans */}
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pb-4">
        {loading ? (
          <div className="text-gray-400 text-center">Loading plans…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6 relative z-0">
            {plans.map((plan) => {
              const popular = plan.id === "pro";
              const current =
                isCurrent(plan.monthlyPriceId) || isCurrent(plan.yearlyPriceId);
              const action = planAction(plan);

              const monthly =
                Number.isFinite(+plan.priceEurMonthly) && +plan.priceEurMonthly >= 0
                  ? +plan.priceEurMonthly
                  : parseEuroFromLabel(plan.priceLabelMonthly) || 0;

              const yearlyTotal =
                Number.isFinite(+plan.priceEurYearly) && +plan.priceEurYearly >= 0
                  ? +plan.priceEurYearly
                  : monthly * 10;

              const discountedMonthly = yearlyTotal / 12;
              const showYearly = billingCycle === "yearly";

              return (
                <div
                  key={plan.id}
                  className="relative rounded-3xl p-6 border backdrop-blur transition-transform duration-200 hover:scale-[1.01] overflow-visible z-0"
                  style={{
                    background: CARD,
                    borderColor: popular ? ACCENT : BORDER,
                    boxShadow: popular
                      ? `0 0 0 2px ${ACCENT}44, 0 12px 30px -12px ${ACCENT}55`
                      : "0 8px 24px rgba(0,0,0,0.22)",
                  }}
                >
                  {popular && (
                    <div className="absolute left-1/2 -top-5 -translate-x-1/2 z-30 flex flex-col items-center">
                      <span
                        className="px-3 py-1 rounded-full text:[11px] text-[11px] font-extrabold tracking-wide uppercase"
                        style={{
                          background: ACCENT,
                          color: "#0B0B16",
                          boxShadow: `0 8px 22px ${ACCENT}55, 0 0 0 2px ${ACCENT}33 inset`,
                          letterSpacing: "0.06em",
                        }}
                      >
                        MOST USED
                      </span>
                      <span
                        className="mt-2 h-[3px] w-56 rounded-full"
                        style={{
                          background: `linear-gradient(90deg, rgba(0,0,0,0) 0%, ${ACCENT} 45%, rgba(0,0,0,0) 100%)`,
                          boxShadow: `0 0 18px ${ACCENT}9A`,
                        }}
                      />
                    </div>
                  )}

                  {current && !cancelAtPeriodEnd && (
                    <div
                      className="absolute top-3 left-3 text-xs px-2 py-1 rounded-full font-semibold z-20"
                      style={{
                        background: "#00D395",
                        color: "#04110D",
                        border: "1px solid #0f5",
                      }}
                    >
                      Your current plan
                    </div>
                  )}
                  {current && cancelAtPeriodEnd && (
                    <div
                      className="absolute top-3 left-3 text-xs px-2 py-1 rounded-full font-semibold z-20"
                      style={{
                        background: "#4b2a2a",
                        color: "#ffd6d6",
                        border: "1px solid #6b2a2a",
                      }}
                    >
                      Cancels at period end
                    </div>
                  )}

                  <div className="flex items-center justify-center gap-2 pt-4">
                    {plan.id === "starter" && (
                      <Star size={20} className="text-white/90" />
                    )}
                    {plan.id === "pro" && <Crown size={20} className="text-white" />}
                    {plan.id === "business" && (
                      <Zap size={20} className="text-white/90" />
                    )}
                    <h3 className="text-white text-2xl font-bold text-center">
                      {plan.name}
                    </h3>
                  </div>

                  <div className="mt-3 text-center">
                    {showYearly ? (
                      <>
                        <div className="text-4xl font-extrabold text-white">
                          {euro2(discountedMonthly)}{" "}
                          <span className="text-base font-semibold">/month</span>
                        </div>
                        <div className="mt-1 text-sm">
                          <span
                            className="line-through font-semibold"
                            style={{ color: RED }}
                          >
                            {euro2(monthly)}
                          </span>
                          <span
                            className="ml-2 inline-flex items-center px-2 py-0.5 text-[11px] rounded-full uppercase tracking-wide"
                            style={{
                              background: "#0d3620",
                              border: "1px solid #134e33",
                              color: "#a7f3d0",
                            }}
                          >
                            2 Months FREE
                          </span>
                        </div>
                        <div className="text-gray-400 text-sm mt-1">
                          {euro2(yearlyTotal)} billed annually
                        </div>
                      </>
                    ) : (
                      <div className="text-4xl font-extrabold text-white">
                        {euro2(monthly)}{" "}
                        <span className="text-base font-semibold">/month</span>
                      </div>
                    )}
                  </div>

                  {/* Capacity at a glance */}
                  <div className="mt-6">
                    <div className="text-xs uppercase tracking-wide text-gray-400 text-center">
                      Capacity at a glance
                    </div>

                    <div className="mt-3 grid grid-cols-3 gap-2">
                      {/* AI Images */}
                      <div className="flex flex-col items-center">
                        <ImageIcon className="w-6 h-6 text-gray-200" />
                        <span className="mt-2 text-gray-200 text-sm sm:text-base">
                          AI Images
                        </span>
                        <div
                          className="mt-3 rounded-2xl px-3 py-4 flex items-center justify-center text-center border w-full"
                          style={{
                            background: "#0f0f21",
                            borderColor: BORDER,
                            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
                          }}
                        >
                          <div className="text-white text-3xl font-extrabold tracking-tight">
                            {plan.imagesCap}
                          </div>
                        </div>
                        <div className="text-center text-gray-500 text-xs mt-1">
                          per month
                        </div>
                      </div>

                      {/* AI Videos */}
                      <div className="flex flex-col items-center">
                        <Film className="w-6 h-6 text-gray-200" />
                        <span className="mt-2 text-gray-200 text-sm sm:text-base">
                          AI Videos
                        </span>
                        <div
                          className="mt-3 rounded-2xl px-3 py-4 flex items-center justify-center text-center border w-full"
                          style={{
                            background:
                              "linear-gradient(180deg, rgba(248,46,136,0.10) 0%, rgba(20,20,40,0.85) 100%)",
                            borderColor: `${ACCENT}55`,
                            boxShadow: `0 0 0 1px ${ACCENT}22 inset`,
                          }}
                        >
                          <div className="text-white text-3xl font-extrabold tracking-tight">
                            {plan.videosCap}
                          </div>
                        </div>
                        <div className="text-center text-gray-500 text-xs mt-1">
                          per month
                        </div>
                      </div>

                      {/* Product Pages */}
                      <div className="flex flex-col items-center">
                        <LinkIcon className="w-6 h-6 text-gray-200" />
                        <span className="mt-2 text-gray-200 text-sm sm:text-base">
                          Pages
                        </span>
                        <div
                          className="mt-3 rounded-2xl px-3 py-4 flex items-center justify-center text-center border w-full"
                          style={{
                            background: "#0f0f21",
                            borderColor: BORDER,
                            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
                          }}
                        >
                          <div className="text-white text-3xl font-extrabold tracking-tight">
                            {plan.analyzeCap}
                          </div>
                        </div>
                        <div className="text-center text-gray-500 text-xs mt-1">
                          per month
                        </div>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={action.onClick}
                    disabled={action.disabled}
                    className="group w-full mt-6 py-3 rounded-xl font-semibold inline-flex items-center justify-center gap-2 transition will-change-transform disabled:opacity-60 disabled:cursor-not-allowed hover:translate-y-[-2px] hover:scale-[1.01]"
                    style={{
                      background:
                        action.kind === "upgrade"
                          ? GREEN
                          : action.kind === "downgrade"
                          ? "#b45309"
                          : ACCENT,
                      color: "#fff",
                      borderColor: "transparent",
                      boxShadow:
                        action.kind === "upgrade"
                          ? "0 10px 26px rgba(34,197,94,0.45)"
                          : action.kind === "downgrade"
                          ? "0 8px 18px rgba(180,83,9,0.35)"
                          : "0 10px 24px rgba(248,46,136,0.35)",
                    }}
                  >
                    {action.loading ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        {action.kind === "downgrade"
                          ? "Opening portal…"
                          : isCurrent(plan.monthlyPriceId) ||
                            isCurrent(plan.yearlyPriceId)
                          ? "Opening portal…"
                          : "Taking you to Stripe…"}
                      </>
                    ) : (
                      <>
                        {action.label}
                        <ChevronRight
                          size={18}
                          className="opacity-80 transition-transform group-hover:translate-x-0.5"
                        />
                      </>
                    )}
                  </button>

                  {action?.helper && (
                    <div className="mt-3 text-xs text-gray-400 flex items-center gap-2">
                      <Info size={14} /> {action.helper}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Common features */}
      {!loading && (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pb-10">
          <div
            className="rounded-3xl border p-6 sm:p-8"
            style={{ borderColor: BORDER, background: "#121228" }}
          >
            <div className="text-center">
              <h3 className="text-white text-2xl font-bold">
                Everything in every plan
              </h3>
              <p className="text-gray-300 mt-2">
                No feature gating. Every tier unlocks the complete toolkit — capacity
                scales with your plan.
              </p>
            </div>

            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {allFeatures.map((f) => (
                <li key={f} className="flex items-start gap-2 text-gray-100">
                  <Check
                    size={18}
                    className="mt-0.5 shrink-0"
                    style={{ color: GREEN }}
                  />
                  <span className="text-sm sm:text-base">{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Add-ons (one-off, available even without subscription) */}
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pb-12">
        {addons.length > 0 && (
          <div
            className="rounded-2xl border p-5 sm:p-6"
            style={{ borderColor: BORDER, background: "#121228" }}
          >
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h3 className="text-white text-lg sm:text-xl font-bold">
                Need more? Add-ons
              </h3>
              <div className="text-gray-400 text-sm">
                {unlimited?.isUnlimited
                  ? "Unlimited enabled — add-ons are unnecessary"
                  : "One-off • delivered instantly"}
              </div>
            </div>

            <div className="mt-4 space-y-4">
              {addons.map((t) => {
                const feature = t.addonFeature; // "images" | "videos" | "urls" | null
                const amount = Number.isFinite(+t.amount)
                  ? Math.max(0, Math.floor(+t.amount))
                  : null;

                const defaultAmt =
                  feature === "images"
                    ? 10
                    : feature === "videos"
                    ? 1
                    : feature === "urls"
                    ? 1
                    : 1;

                const effectiveAmt = amount ?? defaultAmt;

                const sub =
                  feature === "urls"
                    ? `+${effectiveAmt} Product Page${effectiveAmt > 1 ? "s" : ""}`
                    : feature === "videos"
                    ? `+${effectiveAmt} video${effectiveAmt > 1 ? "s" : ""}`
                    : feature === "images"
                    ? `+${effectiveAmt} images`
                    : "";

                const disabled =
                  buttonLoading === t.priceId || unlimited?.isUnlimited;

                const unitPrice = t.unitPrice ?? parseEuroFromLabel(t.label);
                const qty = getQty(t.priceId);
                const totalStr = unitPrice != null ? euro2(unitPrice * qty) : null;

                const resultCount = qty * effectiveAmt;

                const resultStr =
                  feature === "urls"
                    ? `${resultCount} Product Page${resultCount > 1 ? "s" : ""}`
                    : feature === "images"
                    ? `${resultCount} images`
                    : feature === "videos"
                    ? `${resultCount} video${resultCount > 1 ? "s" : ""}`
                    : `${qty} unit${qty > 1 ? "s" : ""}`;

                return (
                  <div
                    key={t.id}
                    className="rounded-2xl border px-4 py-4 sm:py-5"
                    style={{ borderColor: BORDER, background: CARD }}
                  >
                    <div
                      className="
                        flex flex-col gap-3
                        lg:grid lg:gap-4
                        lg:grid-cols-[1fr_160px_1fr_auto]
                        lg:items-center
                      "
                    >
                      <div className="min-w-0">
                        <div className="text-white text-lg font-semibold truncate">
                          {t.label}
                        </div>
                        <div className="text-gray-400 text-sm">{sub}</div>
                      </div>

                      <div className="flex items-center gap-3 w-full lg:w-[160px]">
                        <div className="text-gray-300 text-sm shrink-0">
                          Quantity
                        </div>
                        <div className="flex-1">
                          <QtySelect
                            value={qty}
                            onChange={(v) => setQty(t.priceId, v)}
                            disabled={disabled}
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-start lg:justify-center min-w-0">
                        {totalStr && (
                          <div
                            className="rounded-2xl px-4 py-2 text-white text-sm font-medium"
                            style={{
                              background: "#0f0f21",
                              border: `1px solid ${BORDER}`,
                            }}
                          >
                            You’ll get{" "}
                            <span className="font-extrabold">{resultStr}</span> for{" "}
                            <span className="font-extrabold">{totalStr}</span>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center justify-start lg:justify-end">
                        <button
                          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white transition disabled:opacity-60 disabled:cursor-not-allowed hover:translate-y-[-1px]"
                          style={{
                            background: GREEN,
                            boxShadow: "0 8px 20px rgba(34,197,94,0.35)",
                          }}
                          disabled={disabled}
                          onClick={() => {
                            if (unlimited?.isUnlimited) return;
                            goCheckout({
                              mode: "payment",
                              priceId: t.priceId,
                              quantity: qty,
                              overrides: {
                                kind: "addon",
                                addonId: t.id,
                                unitPrice,
                                amount: effectiveAmt,
                              },
                            });
                          }}
                          title={
                            unlimited?.isUnlimited
                              ? "Unlimited enabled — payments disabled"
                              : ""
                          }
                        >
                          {buttonLoading === t.priceId ? (
                            <>
                              <Loader2 size={16} className="animate-spin" /> Processing…
                            </>
                          ) : (
                            <>
                              Buy now <ChevronRight size={16} className="opacity-80" />
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
